'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.0.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Init off-canvas after mobile menu is ready
		document.addEventListener('mobilemenu-init-done', () => {
			const offCanvas = new OffCanvas({
				debug: false,
				overlay: true
			});
		});

		// Init mobile menu
		const mobileMenu = new MobileMenu();
		mobileMenu.init();

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			...options
		};

		// Call individual init methods
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initAccordions();
		this.initEmbeddedVideos();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	 * Initialize image links
	 *
	 * Find all links to images and use GLightbox to display them.
	 */
	initImageLinks() {
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);

		if (!parentNode) return;

		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			let figcaption = link.parentNode.querySelector('figcaption');
			let caption = figcaption ? figcaption.textContent : '';

			link.addEventListener('click', e => {
				e.preventDefault();

				const lightbox = GLightbox();
				lightbox.setElements([
					{
						href: link.href,
						type: 'image',
						title: caption
					}
				]);

				lightbox.open();
			});
		});
	}

	/**
	 * Initialize accordions
	 */
	initAccordions() {

		// bail out early if there are no accordion items on current page
		const accordionItems = document.querySelectorAll('.js-accordion-item');
		if (!accordionItems.length) return;

		accordionItems.forEach(accordionItem => {

			// accordion item title and content containers
			const accordionItemTitle = accordionItem.querySelector('.js-accordion-item-title');
			const accordionItemContent = accordionItem.querySelector('.js-accordion-item-content');
			accordionItemTitle.setAttribute('id', accordionItem.getAttribute('id') + '__title');
			accordionItemContent.setAttribute('id', accordionItem.getAttribute('id') + '__content');
			accordionItemContent.setAttribute('aria-labelledby', accordionItemTitle.getAttribute('id'));
			accordionItemContent.classList.add('hidden');
			accordionItemContent.setAttribute('hidden', '')

			// accordion item toggle
			const accordionItemToggle = document.createElement('button');
			accordionItemToggle.setAttribute('type', 'button');
			accordionItemToggle.setAttribute('aria-controls', accordionItemContent.getAttribute('id'));
			accordionItemToggle.setAttribute('aria-expanded', false);
			accordionItemToggle.classList.add('js-accordion-item-toggle');
			accordionItemToggle.classList.add('accordion-item');
			accordionItemTitle.parentNode.prepend(accordionItemToggle);
			accordionItemToggle.appendChild(accordionItemTitle);

			// accordion item toggle icon container
			const accordionItemToggleInner = document.createElement('span');
			accordionItemToggleInner.classList.add('accordion-item__inner');
			accordionItemToggleInner.setAttribute('aria-hidden', true);
			accordionItemTitle.appendChild(accordionItemToggleInner);

			// accordion item toggle icon
			const accordionItemToggleIcon = document.createElement('i');
			accordionItemToggleIcon.classList.add('accordion-item__icon');
			accordionItemToggleIcon.classList.add('icon-plus');
			accordionItemToggleInner.appendChild(accordionItemToggleIcon);

			// accordion item event listener
			accordionItemToggle.addEventListener('click', event => {
				accordionItemToggleIcon.classList.toggle('icon-plus');
				accordionItemToggleIcon.classList.toggle('icon-minus');
				accordionItemContent.classList.toggle('hidden');
				if (accordionItemContent.classList.contains('hidden')) {
					accordionItemContent.setAttribute('hidden', '');
					accordionItemToggle.setAttribute('aria-expanded', false);
				} else {
					accordionItemContent.removeAttribute('hidden');
					accordionItemToggle.setAttribute('aria-expanded', true);
					history.pushState(null, null, '#' + accordionItem.getAttribute('id'));
					if ('scrollBehavior' in document.documentElement.style) {
						window.scrollTo({
							top: accordionItemContent.getBoundingClientRect().top + window.pageYOffset,
							behavior: 'smooth'
						});
						accordionItemContent.focus({
							preventScroll: true
						});
					} else {
						// Be nice to IE
						window.scrollTo(0, accordionItemContent.getBoundingClientRect().top + window.pageYOffset);
						window.setTimeout(() => {
							accordionItemContent.focus();
						}, 300);
					}
				}
			});
		})

		// toggle accordion item if matching hash found
		if (window.location.hash) {
			const startItem = document.querySelector('.js-accordion-item' + window.location.hash);
			if (startItem) {
				window.setTimeout(() => {
					startItem.querySelector('.js-accordion-item-toggle').click();
				}, 100);
			}
		}
	}

	/**
	 * Init embedded videos
	 */
	initEmbeddedVideos() {

		// bail out early if there are no videos on this page
		const videos = document.querySelectorAll('.js-embedded-video');
		if (!videos.length) return;

		videos.forEach(video => {
			video.addEventListener('click', event => {
				event.preventDefault();
				const videoContainer = video.closest('.js-embedded-video-container');
				const videoFrame = document.getElementById('js-video-' + video.getAttribute('data-video-id'));
				if (videoFrame) {
					videoFrame.removeAttribute('hidden');
					videoContainer.classList.add('js-show-video');
				}
			});
		});
	}

}

window._site = new Site();
